import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  TablePagination,
  Grid,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Visibility,
  Edit,
  Delete,
  Refresh,
  FilterList,
  WhatsApp,
  Phone,
  Search,
  Clear,
} from '@mui/icons-material';
import { orderAPI, lookupAPI, OrderFilters } from '../services/api';
import { OrderRequest, Store } from '../types';
import { useAuth } from '../contexts/AuthContext';
import dayjs, { Dayjs } from 'dayjs';

// Helper function to convert UTC to IST
const formatDateInIST = (dateString: string, format: string = 'MMM DD, YYYY'): string => {
  // Parse the UTC date and add IST offset (UTC+5:30)
  return dayjs(dateString).add(5, 'hour').add(30, 'minute').format(format);
};

// Helper function to generate WhatsApp message link
const generateWhatsAppLink = (order: OrderRequest): string => {
  const customerName = order.customer_name || 'Customer';
  const storeName = order.store_name || 'Store';
  const mobile = order.customer_number?.toString();
  
  if (!mobile) return '';
  
  // Add India country code if not present
  const formattedMobile = mobile.startsWith('91') ? mobile : `91${mobile}`;
  
  let message = '';
  
  switch (order.status) {
    case 'delivery-pending':
      message = `Dear ${customerName}, your items ordered in FirstShield Pharmacy is ready and available for pickup.`;
      break;
    case 'not-available':
      message = `Dear ${customerName}, We are extremely sorry, we are unable to find the items you ordered at the moment.`;
      break;
    case 'partially-available':
      message = `Dear ${customerName}, we are extremely sorry, some of the medicines you ordered are not available at the moment. The available items are ready for pickup`;
      break;
    default:
      return '';
  }
  
  message += `\nThanks,\nFirstShield Pharmacy ${storeName}`;
  
  return `https://wa.me/${formattedMobile}?text=${encodeURIComponent(message)}`;
};

const OrderView: React.FC = () => {
  const { dbUser } = useAuth();
  const [orders, setOrders] = useState<OrderRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Helper function to check user permissions
  const hasPermission = (permission: string): boolean => {
    if (!dbUser?.permission) return false;
    const userPermissions = dbUser.permission.split(',').map(p => p.trim());
    return userPermissions.includes(permission);
  };
  const [selectedOrder, setSelectedOrder] = useState<OrderRequest | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState<'pending' | 'ordered' | 'delivery-pending' | 'delivered' | 'cancelled' | 'not-available' | 'partially-available'>('pending');
  const [itemInventory, setItemInventory] = useState<{[key: string]: { quantity: number; message: string } | null}>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(5, 'day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());
  const [filtersLoading, setFiltersLoading] = useState(false);
  
  // Advanced search states
  const [searchCustomerName, setSearchCustomerName] = useState<string>('');
  const [searchCustomerPhone, setSearchCustomerPhone] = useState<string>('');
  const [searchStatus, setSearchStatus] = useState<string>('all');
  const [searchMedicineName, setSearchMedicineName] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadStores();
    loadOrders();
  }, []);

  const loadStores = async () => {
    try {
      const storesData = await lookupAPI.getStores();
      setStores(storesData);
    } catch (error) {
      console.error('Error loading stores:', error);
      setAlert({ type: 'error', message: 'Error loading stores' });
    }
  };

  const loadOrders = async (applyFilters = false, customFilters?: OrderFilters, resetPagination = true) => {
    if (applyFilters) {
      setFiltersLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Use custom filters if provided, otherwise build from current state
      const filters: OrderFilters = customFilters ? { ...customFilters } : {};
      
      // If no custom filters provided, build from current state
      if (!customFilters) {
        if (selectedStoreId && selectedStoreId !== 'all') {
          filters.store_id = selectedStoreId;
        }
        
        if (dateFrom) {
          filters.date_from = dateFrom.format('YYYY-MM-DD');
        }
        
        if (dateTo) {
          filters.date_to = dateTo.format('YYYY-MM-DD');
        }
        
        // Include search parameters in default loading
        if (searchCustomerName.trim()) {
          filters.customer_name = searchCustomerName.trim();
        }
        
        if (searchCustomerPhone.trim()) {
          filters.customer_phone = searchCustomerPhone.trim();
        }
        
        if (searchStatus && searchStatus !== 'all') {
          filters.status = searchStatus;
        }
        
        if (searchMedicineName.trim()) {
          filters.medicine_name = searchMedicineName.trim();
        }
      }
      
      console.log('Frontend sending filters:', filters); // Debug log
      
      const ordersData = await orderAPI.getAll(filters);
      setOrders(ordersData);
      if (resetPagination) {
        setPage(0); // Reset pagination when filters change
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setAlert({ type: 'error', message: 'Error loading orders' });
    } finally {
      setLoading(false);
      setFiltersLoading(false);
    }
  };

  const fetchInventoryData = async (order: OrderRequest) => {
    if (!order.items || !order.store_id) return;
    
    const inventoryData: {[key: string]: { quantity: number; message: string } | null} = {};
    
    try {
      // Fetch inventory for each medicine item
      for (const item of order.items) {
        if (item.manufacturer_id && order.store_id) {
          try {
            const inventory = await lookupAPI.getMedicineInventory(item.manufacturer_id, order.store_id);
            if (inventory && inventory.quantity > 0) {
              inventoryData[`${item.manufacturer_id}_${order.store_id}`] = {
                quantity: inventory.quantity,
                message: `Last snapshot: ${inventory.quantity}`
              };
            } else {
              inventoryData[`${item.manufacturer_id}_${order.store_id}`] = {
                quantity: 0,
                message: 'No inventory data'
              };
            }
          } catch (error) {
            console.error(`Error fetching inventory for item ${item.manufacturer_id}:`, error);
            inventoryData[`${item.manufacturer_id}_${order.store_id}`] = {
              quantity: 0,
              message: 'Unable to fetch'
            };
          }
        }
      }
      
      setItemInventory(inventoryData);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    }
  };

  const handleViewOrder = async (order: OrderRequest) => {
    setSelectedOrder(order);
    setViewDialog(true);
    // Fetch inventory data for this order
    await fetchInventoryData(order);
  };

  const handleStatusChange = (order: OrderRequest) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;

    try {
      const updatedOrder = await orderAPI.updateStatus(selectedOrder.id!, newStatus);
      setAlert({ type: 'success', message: 'Status updated successfully!' });
      
      // Update the order in the current list without reloading
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === selectedOrder.id 
            ? { ...order, status: newStatus, updated_at: updatedOrder.updated_at }
            : order
        )
      );
      
      setStatusDialog(false);
    } catch (error) {
      console.error('Error updating status:', error);
      setAlert({ type: 'error', message: 'Error updating status' });
    }
  };

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
    
    // Create filters with the new store value
    const filters: { store_id?: string; date_from?: string; date_to?: string } = {};
    if (storeId && storeId !== 'all') {
      filters.store_id = storeId;
    }
    if (dateFrom) {
      filters.date_from = dateFrom.format('YYYY-MM-DD');
    }
    if (dateTo) {
      filters.date_to = dateTo.format('YYYY-MM-DD');
    }
    
    loadOrders(true, filters);
  };

  const handleDateFromChange = (date: Dayjs | null) => {
    setDateFrom(date);
    
    // Create filters with the new date from value
    const filters: { store_id?: string; date_from?: string; date_to?: string } = {};
    if (selectedStoreId && selectedStoreId !== 'all') {
      filters.store_id = selectedStoreId;
    }
    if (date) {
      filters.date_from = date.format('YYYY-MM-DD');
    }
    if (dateTo) {
      filters.date_to = dateTo.format('YYYY-MM-DD');
    }
    
    loadOrders(true, filters);
  };

  const handleDateToChange = (date: Dayjs | null) => {
    setDateTo(date);
    
    // Create filters with the new date to value
    const filters: { store_id?: string; date_from?: string; date_to?: string } = {};
    if (selectedStoreId && selectedStoreId !== 'all') {
      filters.store_id = selectedStoreId;
    }
    if (dateFrom) {
      filters.date_from = dateFrom.format('YYYY-MM-DD');
    }
    if (date) {
      filters.date_to = date.format('YYYY-MM-DD');
    }
    
    loadOrders(true, filters);
  };

  const handleClearFilters = () => {
    setSelectedStoreId('all');
    setDateFrom(dayjs().subtract(5, 'day'));
    setDateTo(dayjs());
    setSearchCustomerName('');
    setSearchCustomerPhone('');
    setSearchStatus('all');
    setSearchMedicineName('');
    
    // Load orders with only default date filters
    const defaultFilters: OrderFilters = {
      date_from: dayjs().subtract(5, 'day').format('YYYY-MM-DD'),
      date_to: dayjs().format('YYYY-MM-DD')
    };
    loadOrders(true, defaultFilters);
  };

  const handleSearch = () => {
    const filters: OrderFilters = {};
    
    // Basic filters
    if (selectedStoreId && selectedStoreId !== 'all') {
      filters.store_id = selectedStoreId;
    }
    
    if (dateFrom) {
      filters.date_from = dateFrom.format('YYYY-MM-DD');
    }
    
    if (dateTo) {
      filters.date_to = dateTo.format('YYYY-MM-DD');
    }
    
    // Search filters
    if (searchCustomerName.trim()) {
      filters.customer_name = searchCustomerName.trim();
    }
    
    if (searchCustomerPhone.trim()) {
      filters.customer_phone = searchCustomerPhone.trim();
    }
    
    if (searchStatus && searchStatus !== 'all') {
      filters.status = searchStatus;
    }
    
    if (searchMedicineName.trim()) {
      filters.medicine_name = searchMedicineName.trim();
    }
    
    loadOrders(true, filters);
  };

  const handleClearSearch = () => {
    setSearchCustomerName('');
    setSearchCustomerPhone('');
    setSearchStatus('all');
    setSearchMedicineName('');
    
    // Reload with just basic filters
    const basicFilters: OrderFilters = {};
    if (selectedStoreId && selectedStoreId !== 'all') {
      basicFilters.store_id = selectedStoreId;
    }
    if (dateFrom) {
      basicFilters.date_from = dateFrom.format('YYYY-MM-DD');
    }
    if (dateTo) {
      basicFilters.date_to = dateTo.format('YYYY-MM-DD');
    }
    
    loadOrders(true, basicFilters);
  };

  const handleDeleteOrder = async (order: OrderRequest) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await orderAPI.delete(order.id!);
        setAlert({ type: 'success', message: 'Order deleted successfully!' });
        // Reload with current filters but preserve pagination
        loadOrders(true, undefined, false);
      } catch (error) {
        console.error('Error deleting order:', error);
        setAlert({ type: 'error', message: 'Error deleting order' });
      }
    }
  };

  const handlePhoneClick = (order: OrderRequest) => {
    if (!order.customer_number) {
      setAlert({ type: 'error', message: 'Customer mobile number is not available' });
      return;
    }

    const mobile = order.customer_number.toString();
    // Add India country code if not present
    const formattedMobile = mobile.startsWith('91') ? mobile : `91${mobile}`;
    
    // Create tel: URI for phone call
    const phoneLink = `tel:+${formattedMobile}`;
    
    // Open phone dialer
    window.location.href = phoneLink;
  };

  const handleWhatsAppClick = async (order: OrderRequest) => {
    // Check if status allows WhatsApp message generation
    const allowedStatuses = ['delivery-pending', 'not-available', 'partially-available'];
    
    if (!allowedStatuses.includes(order.status)) {
      setAlert({ 
        type: 'error', 
        message: 'WhatsApp message can only be generated for orders with status: Delivery-pending, Not-available, or Partially-available' 
      });
      return;
    }

    if (!order.customer_number) {
      setAlert({ type: 'error', message: 'Customer mobile number is required to send WhatsApp message' });
      return;
    }

    const whatsappLink = generateWhatsAppLink(order);
    if (whatsappLink) {
      // Open WhatsApp link
      window.open(whatsappLink, '_blank');
      
      // Update message status to 'send'
      try {
        await orderAPI.updateMessageStatus(order.id!, 'send', dbUser?.email || 'system');
        
        // Update the order in the current list
        setOrders(prevOrders => 
          prevOrders.map(o => 
            o.id === order.id 
              ? { ...o, message_status: 'send' }
              : o
          )
        );
        
        setAlert({ type: 'success', message: 'WhatsApp message opened and status updated!' });
      } catch (error) {
        console.error('Error updating message status:', error);
        setAlert({ type: 'error', message: 'WhatsApp opened but failed to update message status' });
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'ordered':
        return 'info';
      case 'delivery-pending':
        return 'warning';
      case 'cancelled':
        return 'warning'; // Orange color for cancelled
      case 'not-available':
        return 'error'; // Red color for not available
      case 'partially-available':
        return 'warning'; // Orange color for partially available
      case 'pending':
      default:
        return 'error';
    }
  };

  const getRowBackgroundColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return '#e8f5e8'; // Light green
      case 'cancelled':
        return '#fff3e0'; // Light orange
      case 'not-available':
        return '#ffebee'; // Light red for not available
      case 'partially-available':
        return '#fff3e0'; // Light orange for partially available
      default:
        return '#ffebee'; // Light red for pending/other statuses
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" color="primary">
            Order Records
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadOrders(false, undefined, false)} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {alert && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert(null)}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filter Controls */}
        <Card elevation={2} sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterList sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary">
                Filter Orders
              </Typography>
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Store</InputLabel>
                  <Select
                    value={selectedStoreId}
                    onChange={(e) => handleStoreChange(e.target.value as string)}
                    label="Store"
                    disabled={filtersLoading}
                  >
                    <MenuItem value="all">All Stores</MenuItem>
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id.toString()}>
                        {store.store_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From Date"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  disabled={filtersLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={dateTo}
                  onChange={handleDateToChange}
                  disabled={filtersLoading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={filtersLoading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
            
            {filtersLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Advanced Search Controls */}
        <Card elevation={2} sx={{ mb: 3, bgcolor: '#f0f7ff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Search sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" color="primary">
                Advanced Search
              </Typography>
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={searchCustomerName}
                  onChange={(e) => setSearchCustomerName(e.target.value)}
                  disabled={filtersLoading}
                  placeholder="Search by customer name..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={searchCustomerPhone}
                  onChange={(e) => setSearchCustomerPhone(e.target.value)}
                  disabled={filtersLoading}
                  placeholder="Search by phone..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={searchStatus}
                    onChange={(e) => setSearchStatus(e.target.value as string)}
                    label="Status"
                    disabled={filtersLoading}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="ordered">Ordered</MenuItem>
                    <MenuItem value="delivery-pending">Delivery Pending</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="not-available">Not Available</MenuItem>
                    <MenuItem value="partially-available">Partially Available</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Medicine Name"
                  value={searchMedicineName}
                  onChange={(e) => setSearchMedicineName(e.target.value)}
                  disabled={filtersLoading}
                  placeholder="Search by medicine name..."
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={filtersLoading}
                  fullWidth
                  sx={{ height: '56px' }}
                  startIcon={<Search />}
                >
                  Search
                </Button>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  disabled={filtersLoading}
                  fullWidth
                  sx={{ height: '56px' }}
                  startIcon={<Clear />}
                >
                  Clear Search
                </Button>
              </Grid>
            </Grid>
            
            {filtersLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Total Orders: {orders.length} | 
          <span style={{ color: '#4caf50', fontWeight: 'bold' }}> Green: Delivered</span> | 
          <span style={{ color: '#ff9800', fontWeight: 'bold' }}> Orange: Cancelled/Partially Available</span> | 
          <span style={{ color: '#f44336', fontWeight: 'bold' }}> Red: Pending/Not Available/Other Status</span>
        </Typography>

        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Store</strong></TableCell>
                <TableCell><strong>Staff</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Mobile</strong></TableCell>
                <TableCell><strong>Items</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>WhatsApp</strong></TableCell>
                <TableCell><strong>Message Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map((order) => (
                <TableRow
                  key={order.id}
                  sx={{
                    backgroundColor: getRowBackgroundColor(order.status),
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{formatDateInIST(order.date)}</TableCell>
                  <TableCell>{order.store_name || 'N/A'}</TableCell>
                  <TableCell>{order.staff_name || 'N/A'}</TableCell>
                  <TableCell>{order.customer_name || 'N/A'}</TableCell>
                  <TableCell>{order.customer_number || 'N/A'}</TableCell>
                  <TableCell>
                    {order.items.length > 0 ? (
                      <Tooltip title={order.items.map(item => 
                        item.medicine_name || item.title || 'Unknown medicine'
                      ).join(', ')}>
                        <Chip
                          label={`${order.items.length} item(s)`}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      'No items'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      color={getStatusColor(order.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {order.customer_number ? (
                      <Tooltip title="Call Customer">
                        <IconButton
                          size="small"
                          onClick={() => handlePhoneClick(order)}
                          color="primary"
                          sx={{ color: '#1976d2' }}
                        >
                          <Phone />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {['delivery-pending', 'not-available', 'partially-available'].includes(order.status) && order.customer_number ? (
                      <Tooltip title="Send WhatsApp Message">
                        <IconButton
                          size="small"
                          onClick={() => handleWhatsAppClick(order)}
                          color="success"
                          sx={{ color: '#25D366' }}
                        >
                          <WhatsApp />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.message_status === 'send' ? 'Sent' : 'Not Generated'}
                      color={order.message_status === 'send' ? 'success' : 'default'}
                      size="small"
                      variant={order.message_status === 'send' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order)}
                          color="info"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {hasPermission('edit_orders') && (
                        <Tooltip title="Edit Status">
                          <IconButton
                            size="small"
                            onClick={() => handleStatusChange(order)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}
                      {hasPermission('delete_orders') && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOrder(order)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={orders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* View Order Dialog */}
        <Dialog open={viewDialog} onClose={() => {
          setViewDialog(false);
          setItemInventory({}); // Clear inventory data when closing
        }} maxWidth="md" fullWidth>
          <DialogTitle>Order Details</DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Order Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography><strong>ID:</strong> {selectedOrder.id}</Typography>
                  <Typography><strong>Date:</strong> {formatDateInIST(selectedOrder.date, 'MMMM DD, YYYY')}</Typography>
                  <Typography><strong>Store:</strong> {selectedOrder.store_name}</Typography>
                  <Typography><strong>Staff:</strong> {selectedOrder.staff_name}</Typography>
                  <Typography><strong>Customer:</strong> {selectedOrder.customer_name || 'N/A'}</Typography>
                  <Typography><strong>Mobile:</strong> {selectedOrder.customer_number || 'N/A'}</Typography>
                  <Typography><strong>Status:</strong> 
                    <Chip
                      label={selectedOrder.status}
                      color={getStatusColor(selectedOrder.status)}
                      size="small"
                      sx={{ ml: 1, textTransform: 'capitalize' }}
                    />
                  </Typography>
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  Medicine Items
                </Typography>
                <TableContainer component={Paper} elevation={1}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Medicine Name</strong></TableCell>
                        <TableCell><strong>Manufacturer</strong></TableCell>
                        <TableCell><strong>Combination</strong></TableCell>
                        <TableCell><strong>Pack Size</strong></TableCell>
                        <TableCell><strong>Quantity</strong></TableCell>
                        <TableCell><strong>Inventory Quantity</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {item.medicine_name || item.title || 'Unknown medicine'}
                              </Typography>
                              {item.medicine_name && item.title !== item.medicine_name && (
                                <Typography variant="caption" color="textSecondary">
                                  ({item.title})
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{item.manufacturer_name || 'N/A'}</TableCell>
                          <TableCell>{item.combination || 'N/A'}</TableCell>
                          <TableCell>{item.pack_size || 'N/A'}</TableCell>
                          <TableCell>{item.quantity || 'N/A'}</TableCell>
                          <TableCell>
                            {item.manufacturer_id && selectedOrder?.store_id ? (
                              (() => {
                                const key = `${item.manufacturer_id}_${selectedOrder.store_id}`;
                                const inventory = itemInventory[key];
                                if (inventory) {
                                  return (
                                    <Box>
                                      <Typography variant="body2" fontWeight="bold">
                                        {inventory.quantity}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {inventory.message}
                                      </Typography>
                                    </Box>
                                  );
                                }
                                return <Typography variant="body2" color="textSecondary">Loading...</Typography>;
                              })()
                            ) : (
                              <Typography variant="body2" color="textSecondary">N/A</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
          <DialogTitle>Update Order Status</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as 'pending' | 'ordered' | 'delivery-pending' | 'delivered' | 'cancelled' | 'not-available' | 'partially-available')}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="ordered">Ordered</MenuItem>
                <MenuItem value="delivery-pending">Delivery Pending</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="not-available">Not Available</MenuItem>
                <MenuItem value="partially-available">Partially Available</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default OrderView;
